/**
 * MCP tools for test suite (workflow test execution) results and triggering.
 *
 * Mirrors `canary test-suite run`, `list`, `get`, `diagnostics`, and `steps`.
 *
 * @module
 */

import { mcpApiRequest } from '../api-client';
import { toolJson } from '../tool-helpers';

export function getTestSuiteToolDefinitions() {
  return [
    {
      name: 'canary_test_suite_run',
      description:
        'Trigger a test suite execution. Returns the suite ID and job ID. Use canary_test_suite_get to check results after triggering.',
      inputSchema: {
        type: 'object',
        properties: {
          tag: {
            type: 'string',
            description: 'Filter workflows by tag',
          },
          namePattern: {
            type: 'string',
            description: 'Filter workflows by name pattern (regex)',
          },
          propertyId: {
            type: 'string',
            description: 'Target a specific property by UUID',
          },
          environmentId: {
            type: 'string',
            description: 'Target a specific environment by UUID',
          },
        },
      },
    },
    {
      name: 'canary_test_suite_list',
      description:
        'List recent test suite executions with pass/fail counts, ordered by most recent first.',
      inputSchema: {
        type: 'object',
        properties: {
          page: {
            type: 'number',
            description: 'Page number (default: 1)',
          },
          pageSize: {
            type: 'number',
            description: 'Page size (default: 20)',
          },
        },
      },
    },
    {
      name: 'canary_test_suite_get',
      description:
        'Get test suite detail including all individual test runs and their results.',
      inputSchema: {
        type: 'object',
        properties: {
          executionId: {
            type: 'string',
            description: 'The test execution UUID',
          },
        },
        required: ['executionId'],
      },
    },
    {
      name: 'canary_test_suite_diagnostics',
      description:
        'Get AI-generated failure diagnostic for a specific workflow run, including root cause analysis and recommendations.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: {
            type: 'string',
            description: 'The workflow UUID',
          },
          runId: {
            type: 'string',
            description: 'The workflow run UUID',
          },
        },
        required: ['workflowId', 'runId'],
      },
    },
    {
      name: 'canary_test_suite_steps',
      description:
        'Get the individual steps of a workflow run with their status, duration, and error messages.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: {
            type: 'string',
            description: 'The workflow UUID',
          },
          runId: {
            type: 'string',
            description: 'The workflow run UUID',
          },
        },
        required: ['workflowId', 'runId'],
      },
    },
  ];
}

export async function handleTestSuiteTool(
  tool: string,
  args: Record<string, unknown>
) {
  if (tool === 'canary_test_suite_run') {
    try {
      const params = new URLSearchParams();
      if (args.tag) params.set('tag', String(args.tag));
      if (args.namePattern) params.set('namePattern', String(args.namePattern));

      const queryStr = params.toString();
      const path = `/workflows/test-runs${queryStr ? `?${queryStr}` : ''}`;

      const body: Record<string, string> = {};
      if (args.propertyId) body.propertyId = String(args.propertyId);
      if (args.environmentId) body.environmentId = String(args.environmentId);

      const data = await mcpApiRequest<{
        ok: boolean;
        suiteId?: string;
        jobId?: string;
        appUrl?: string;
        error?: string;
      }>('POST', path, body);

      if (!data.ok) {
        return toolJson({ ok: false, error: data.error ?? 'Unknown error' });
      }

      return toolJson({
        ok: true,
        suiteId: data.suiteId,
        jobId: data.jobId,
        appUrl: data.appUrl,
        message: `Test suite triggered. Use canary_test_suite_get or canary_test_suite_list to check results.`,
      });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_test_suite_list') {
    try {
      const params = new URLSearchParams();
      params.set('page', String(args.page ?? 1));
      params.set('pageSize', String(args.pageSize ?? 20));
      params.set('sortBy', 'createdAt');
      params.set('sortOrder', 'desc');

      const result = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        data: unknown[];
        pagination: unknown;
      }>('GET', `/workflows/test-executions?${params}`);

      if (!result.ok) {
        return toolJson({ ok: false, error: result.error ?? 'Unknown error' });
      }

      return toolJson({ ok: true, data: result.data, pagination: result.pagination });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_test_suite_get') {
    try {
      const executionId = args.executionId as string;
      const result = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        execution: unknown;
        testRuns: unknown[];
      }>('GET', `/workflows/test-executions/${executionId}`);

      if (!result.ok) {
        return toolJson({ ok: false, error: result.error ?? 'Unknown error' });
      }

      return toolJson({ ok: true, execution: result.execution, testRuns: result.testRuns });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_test_suite_diagnostics') {
    try {
      const workflowId = args.workflowId as string;
      const runId = args.runId as string;

      const result = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        data: unknown;
      }>('GET', `/workflows/${workflowId}/runs/${runId}/failure-diagnostic`);

      if (!result.ok) {
        return toolJson({ ok: false, error: result.error ?? 'Unknown error' });
      }

      return toolJson({ ok: true, data: result.data });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_test_suite_steps') {
    try {
      const workflowId = args.workflowId as string;
      const runId = args.runId as string;

      const result = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        data: unknown;
      }>('GET', `/workflows/${workflowId}/runs/${runId}`);

      if (!result.ok) {
        return toolJson({ ok: false, error: result.error ?? 'Unknown error' });
      }

      return toolJson({ ok: true, data: result.data });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  return null;
}
